// Library imports
import axios from "axios";
import * as config from "../config";

// Vite-native dev mode check
const isDev = import.meta.env.DEV;

// Debug logging (only in development)
if (isDev) {
  console.log("=== BACKEND.JS INIT ===");
  console.log("API Base URL:", import.meta.env.VITE_BACKEND_API_URL);
  console.log("API Key set:", !!import.meta.env.VITE_BACKEND_API_KEY);
  console.log("=======================");
}

/**
 * Axios client instance for API calls
 * @type {import('axios').AxiosInstance}
 */
const client = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_API_URL || "",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 second timeout
});

// API key interceptor
client.interceptors.request.use(requestConfig => {
  const apiKey = import.meta.env.VITE_BACKEND_API_KEY;
  if (apiKey) {
    requestConfig.headers = requestConfig.headers ?? {};
    requestConfig.headers["x-api-key"] = apiKey;
  }
  return requestConfig;
});

// Response error interceptor (better error handling)
client.interceptors.response.use(
  response => response,
  error => {
    if (isDev) {
      console.error("API Error:", {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      });
    }
    return Promise.reject(error);
  }
);

/**
 * Helper function to parse Lambda responses
 * @param {import('axios').AxiosResponse} response - Axios response object
 * @returns {import('axios').AxiosResponse} Normalized response
 */
const normalizeResponse = response => {
  const body = response?.data?.body;
  if (typeof body !== "string") return response;

  try {
    const parsedBody = JSON.parse(body);
    return {
      ...response,
      data: {
        ...response.data,
        ...parsedBody,
      },
    };
  } catch (parseError) {
    if (isDev) {
      console.error("Response parse error:", parseError);
    }
    throw new Error("Invalid response format from server");
  }
};

/**
 * Singpass login - Initiate OAuth flow
 * @returns {Promise<import('axios').AxiosResponse>} API response with redirect_uri
 */
export const SingpassLogin = () => {
  const loginPath = config.backend.route.root + config.backend.route.singpass.login;

  if (isDev) {
    console.log("→ SingpassLogin:", loginPath);
  }

  return client.get(loginPath);
};

/**
 * Singpass callback - Exchange authorization code for user data
 * @param {Object} params - Callback parameters
 * @param {string} params.code - Authorization code from Singpass
 * @param {string} params.state - State parameter for CSRF protection
 * @returns {Promise<import('axios').AxiosResponse>} API response with user info
 */
export const SingpassCallback = ({ code, state }) => {
  const callbackPath = config.backend.route.root + config.backend.route.singpass.callback;

  if (isDev) {
    console.log("→ SingpassCallback:", callbackPath, { code, state });
  }

  return client.post(callbackPath, { code, state });
};

/**
 * Register user for palm access
 * @param {Object} data - User registration data
 * @param {string} data.name - User's full name
 * @param {string} [data.nric] - User's NRIC/FIN
 * @param {string} [data.nric_fin] - Alternative NRIC/FIN field
 * @returns {Promise<import('axios').AxiosResponse>} API response with QR code
 */
export const RegisterPalmAccess = data => {
  const userData = {
    name: data.name,
    nric_fin: data.nric || data.nric_fin,
    // mobile_number: data.number_mobile || data.mobile_number,
    // email: data.email,
  };

  if (isDev) {
    console.log("→ RegisterPalmAccess:", userData);
  }

  // Build the API endpoint path
  const registerPath = config.backend.route.root + config.backend.route.register.root;

  // Make the POST request to the backend
  return client
    .post(registerPath, userData)
    .then(normalizeResponse)
    .catch(error => {
      if (isDev) {
        console.error("RegisterPalmAccess API error:", error);
      }
      throw error;
    });
};
